import styled from '@emotion/styled'
import { faCheckCircle, faCircle } from '@fortawesome/free-regular-svg-icons'
import { faUsers } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useQuery } from '@tanstack/react-query'
import { Eye } from 'lucide-react'
import React, { useContext, useRef, useState } from 'react'
import { Button, Modal } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import Breakpoint from '../../Breakpoint'
import SearchInput from '../../components/global/SearchInput'
import UserContext from '../../context/UserContext'
import { apiClient } from '../../services/apiClient'
import { Exercise, Intensity } from '../../types/exercise.type'
import { TrainingSessionGroup as TrainingSessionGroupType } from '../../types/training-session-group.type'
import CoachMemberCard from './CoachMemberCard'
import ExerciseDetailsModal from './ExerciseDetailsModal'

interface IntensityBadgeProps {
    intensity: Intensity
    children: React.ReactNode
}

interface TrainingSessionStep {
    id?: number
    title: string
    startTime: string
    endTime: string
    description?: string
    exerciseId?: number
}

interface TrainingSession {
    id?: number
    title: string
    description?: string
    intensity: Intensity
    coachId: number
    groupId?: number
    order: number
    group?: TrainingSessionGroupType
    steps: TrainingSessionStep[]
    isCompleted?: boolean
    completedAt?: string
    createdAt?: string
    updatedAt?: string
}

interface TrainingSessionGroupProps {
    group: TrainingSessionGroupType & {
        sessions: TrainingSession[]
    }
    onEdit: (session: TrainingSession) => void
    onDelete: (id: number) => void
    onToggleComplete?: (session: TrainingSession) => Promise<void>
}

const formatTime = (timeString: string) => {
    const [hours, minutes] = timeString.split(':')
    return `${hours}:${minutes}`
}

export const TrainingSessionGroup: React.FC<TrainingSessionGroupProps> = ({
    group,
    onEdit,
    onDelete,
    onToggleComplete,
}) => {
    const [expandedSessions, setExpandedSessions] = useState<Record<number, boolean>>({})
    const [showMembersModal, setShowMembersModal] = useState(false)
    const [showExerciseModal, setShowExerciseModal] = useState(false)
    const [selectedExercise, setSelectedExercise] = useState<Exercise | null>(null)
    const [searchTerm, setSearchTerm] = useState('')
    const sessionsContainerRef = useRef<HTMLDivElement>(null)
    const isDragging = useRef(false)
    const startY = useRef(0)
    const scrollTop = useRef(0)
    const user = useContext(UserContext)

    const handleMouseDown = (e: React.MouseEvent) => {
        // Ne démarrer le défilement que si on clique sur le conteneur de défilement
        if (e.currentTarget === sessionsContainerRef.current) {
            isDragging.current = true
            startY.current = e.pageY - (sessionsContainerRef.current?.offsetTop ?? 0)
            scrollTop.current = sessionsContainerRef.current?.scrollTop ?? 0
            document.body.style.cursor = 'grabbing'
            document.body.style.userSelect = 'none'
            e.preventDefault() // Empêcher la sélection de texte
        }
    }

    const handleMouseMove = React.useCallback(
        (e: MouseEvent) => {
            if (!isDragging.current || !sessionsContainerRef.current) return

            e.preventDefault()
            const y = e.pageY - (sessionsContainerRef.current.getBoundingClientRect().top + window.scrollY)
            const walk = (y - startY.current) * 2
            sessionsContainerRef.current.scrollTop = scrollTop.current - walk
        },
        [isDragging],
    )

    const handleMouseUp = React.useCallback(() => {
        if (!isDragging.current) return
        isDragging.current = false
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
    }, [isDragging])

    // Gestion des événements de souris
    React.useEffect(() => {
        const onMouseMove = (e: MouseEvent) => handleMouseMove(e)

        document.addEventListener('mousemove', onMouseMove)
        document.addEventListener('mouseup', handleMouseUp)
        document.addEventListener('mouseleave', handleMouseUp)

        return () => {
            document.removeEventListener('mousemove', onMouseMove)
            document.removeEventListener('mouseup', handleMouseUp)
            document.removeEventListener('mouseleave', handleMouseUp)
            document.body.style.cursor = ''
            document.body.style.userSelect = ''
        }
    }, [handleMouseMove, handleMouseUp])

    const { t } = useTranslation()

    // Récupération des exercices
    const { data: exercises = [] } = useQuery<Exercise[]>({
        queryKey: ['exercises'],
        queryFn: async (): Promise<Exercise[]> => {
            if (!user) throw new Error('User not authenticated')
            const response = await apiClient.get<Exercise[]>('/exercises')
            return response.data
        },
        enabled: !!user,
    })

    const handleExerciseClick = (exerciseId: number) => {
        const exercise = exercises.find((ex: Exercise) => ex.id === exerciseId)
        if (exercise) {
            setSelectedExercise(exercise)
            setShowExerciseModal(true)
        }
    }

    return (
        <GroupContainer>
            <GroupHeader>
                <GroupTitleWrapper>
                    <GroupTitle>{group.title}</GroupTitle>
                </GroupTitleWrapper>
                <SessionCount>
                    {group.sessions.length} {t('coach.trainingSessions.session')}
                    {group.sessions.length > 1 ? 's' : ''}
                </SessionCount>
                {group.members && group.members.length > 0 && (
                    <MembersButton
                        variant="outline-primary"
                        size="sm"
                        onClick={() => setShowMembersModal(true)}
                        title={t('coach.trainingSessions.viewMembers')}
                    >
                        <FontAwesomeIcon icon={faUsers} />
                        <span>{group.members.length}</span>
                    </MembersButton>
                )}
            </GroupHeader>

            {/* Modal des membres du groupe */}
            <Modal show={showMembersModal} onHide={() => setShowMembersModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        {t('coach.trainingSessions.groupMembers')} - {group.title}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div style={{ marginBottom: '1rem' }}>
                        <SearchInputCustom
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder={t('common.search')}
                        />
                    </div>
                    <MembersGrid>
                        {group.members
                            ?.filter(
                                (member) =>
                                    member.member.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                    member.member.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                    member.member.email.toLowerCase().includes(searchTerm.toLowerCase()),
                            )
                            .map((member) => (
                                <CoachMemberCard key={`member-${member.id}`} user={member.member} />
                            ))}
                    </MembersGrid>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowMembersModal(false)}>
                        {t('common.close')}
                    </Button>
                </Modal.Footer>
            </Modal>

            {group.description && (
                <p
                    style={{
                        margin: '0 0 16px 0',
                        color: '#495057',
                        fontSize: '0.9rem',
                        lineHeight: '1.5',
                    }}
                >
                    {group.description}
                </p>
            )}
            <div
                ref={sessionsContainerRef}
                onMouseDown={handleMouseDown}
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '16px',
                    overflowY: 'auto',
                    scrollBehavior: 'smooth',
                    scrollbarWidth: 'thin',
                    padding: '4px 4px 4px 0',
                    flex: '1 1 auto',
                    cursor: 'grab',
                    position: 'relative',
                }}
                onMouseEnter={() => {
                    if (!isDragging.current) {
                        sessionsContainerRef.current?.style.setProperty('cursor', 'grab')
                    }
                }}
                onMouseLeave={() => {
                    if (!isDragging.current) {
                        sessionsContainerRef.current?.style.setProperty('cursor', '')
                    }
                }}
                onMouseUp={() => {
                    sessionsContainerRef.current?.style.setProperty('cursor', 'grab')
                }}
            >
                {group.sessions
                    .sort((a, b) => a.order - b.order)
                    .map((session) => (
                        <SessionCard key={session.id}>
                            <SessionHeader>
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '8px',
                                        alignItems: 'center',
                                        width: '100%',
                                    }}
                                >
                                    <SessionOrder>{session.order}</SessionOrder>
                                    <SessionTitle>{session.title}</SessionTitle>
                                    <div
                                        style={
                                            window.innerWidth <= +Breakpoint.TABLET_L.split('px')[0]
                                                ? {
                                                      display: 'flex',
                                                      flexDirection: 'column',
                                                      alignItems: 'flex-start',
                                                      gap: '8px',
                                                  }
                                                : { display: 'flex', alignItems: 'center', gap: '8px' }
                                        }
                                    >
                                        {onToggleComplete && (
                                            <CompleteButton
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    onToggleComplete(session)
                                                }}
                                                title={
                                                    session.isCompleted
                                                        ? t('common.markAsIncomplete')
                                                        : t('common.markAsComplete')
                                                }
                                                completed={session.isCompleted}
                                                aria-label={
                                                    session.isCompleted
                                                        ? t('common.markAsIncomplete')
                                                        : t('common.markAsComplete')
                                                }
                                            >
                                                <FontAwesomeIcon
                                                    icon={session.isCompleted ? faCheckCircle : faCircle}
                                                    style={{ marginRight: '4px' }}
                                                />
                                                {session.isCompleted
                                                    ? t('common.completed')
                                                    : t('common.markAsComplete')}
                                            </CompleteButton>
                                        )}
                                        {session.isCompleted && (
                                            <CompletedBadge>
                                                <FontAwesomeIcon icon={faCheckCircle} style={{ marginRight: '4px' }} />
                                                {session.completedAt
                                                    ? new Date(session.completedAt).toLocaleDateString()
                                                    : t('common.completed')}
                                            </CompletedBadge>
                                        )}
                                    </div>
                                </div>
                            </SessionHeader>
                            <SessionDescription>
                                {session.description && (
                                    <div dangerouslySetInnerHTML={{ __html: session.description }} />
                                )}
                            </SessionDescription>
                            <div>
                                <IntensityBadge intensity={session.intensity}>
                                    {t(`intensity.${session.intensity.toLowerCase()}`)}
                                </IntensityBadge>
                            </div>
                            {session.steps && session.steps.length > 0 && (
                                <StepsList>
                                    <StepHeader
                                        onClick={() =>
                                            setExpandedSessions((prev) => ({
                                                ...prev,
                                                [session.id!]: !prev[session.id!],
                                            }))
                                        }
                                        style={{ cursor: 'pointer', marginBottom: '8px' }}
                                    >
                                        <h5
                                            style={{
                                                margin: 0,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                fontSize: '1.4rem',
                                            }}
                                        >
                                            {expandedSessions[session.id!] ? '▼' : '▶'}{' '}
                                            {t('coach.trainingSessions.steps')} ({session.steps.length})
                                        </h5>
                                    </StepHeader>
                                    {expandedSessions[session.id!] &&
                                        [...session.steps].map((step) => (
                                            <StepItem key={`step-${step.id}`}>
                                                <StepHeader>
                                                    <StepTitle>{step.title}</StepTitle>
                                                    <StepTime>
                                                        {formatTime(step.startTime)} - {formatTime(step.endTime)}
                                                    </StepTime>
                                                </StepHeader>
                                                {step.description && (
                                                    <div style={{ marginBottom: '8px' }}>
                                                        <div dangerouslySetInnerHTML={{ __html: step.description }} />
                                                    </div>
                                                )}
                                                {step.exerciseId && (
                                                    <StepExerciseLink
                                                        onClick={(e) => {
                                                            e.stopPropagation()
                                                            handleExerciseClick(step.exerciseId!)
                                                        }}
                                                        title={t('coach.trainingSessions.viewExercise')}
                                                    >
                                                        <Eye size={16} />
                                                        {t('coach.trainingSessions.exercise')}
                                                    </StepExerciseLink>
                                                )}
                                            </StepItem>
                                        ))}
                                </StepsList>
                            )}
                            <div style={{ display: 'flex', gap: '8px', flexDirection: 'column', marginTop: '16px' }}>
                                <Button variant="outline-primary" size="lg" onClick={() => onEdit(session)}>
                                    {t('common.edit')}
                                </Button>
                                <Button
                                    variant="outline-danger"
                                    size="lg"
                                    onClick={() => session.id && onDelete(session.id)}
                                    style={Breakpoint.TABLET_XS ? { width: '100%' } : { marginLeft: '8px' }}
                                >
                                    {t('common.delete')}
                                </Button>
                            </div>
                        </SessionCard>
                    ))}
            </div>

            <ExerciseDetailsModal
                show={showExerciseModal}
                onClose={() => setShowExerciseModal(false)}
                exercise={selectedExercise}
            />
        </GroupContainer>
    )
}

export default TrainingSessionGroup

// Styled components
const SessionCard = styled.div`
    background: #ffffff;
    border-radius: 8px;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
    padding: 18px;
    border: 1px solid #e9ecef;
    transition: all 0.2s ease;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
        border-color: #dee2e6;
    }
`

const SessionHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 16px;
    position: relative;
`

const SessionOrder = styled.span`
    background-color: #f0f0f0;
    border-radius: 50%;
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    font-size: 14px;
    color: #333;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    z-index: 1;

    @media (max-width: ${Breakpoint.TABLET_XS}) {
        width: 26px;
        height: 26px;
        font-size: 13px;
    }

    @media (max-width: ${Breakpoint.PHONE}) {
        width: 24px;
        height: 24px;
        font-size: 12px;
        left: -12px;
        top: -2px;
    }
`

const SessionTitle = styled.h3`
    margin: 0 0 8px 0;
    font-size: 1.6rem;
    font-weight: 600;
    display: flex;
    flex-direction: column;
    gap: 16px;
    align-items: center;
    width: 100%;

    @media (max-width: ${Breakpoint.TABLET_XS}) {
        flex-direction: column;
        align-items: center;
        gap: 16px;
    }
`

const CompletedBadge = styled.span`
    background-color: #28a745;
    color: white;
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 0.8rem;
    font-weight: 500;
    display: inline-flex;
    align-items: center;
    gap: 4px;

    @media (max-width: ${Breakpoint.TABLET_XS}) {
        margin-top: 8px;
    }
`

const CompleteButton = styled.button<{ completed: boolean | undefined }>`
    background: ${(props) => (props.completed ? '#e8f5e9' : 'transparent')};
    border: 2px solid ${(props) => (props.completed ? '#28a745' : '#6c757d')};
    color: ${(props) => (props.completed ? '#28a745' : '#6c757d')};
    cursor: pointer;
    padding: 8px 12px;
    border-radius: 20px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 0.9rem;
    font-weight: 500;
    transition: all 0.2s ease;

    &:hover {
        background: ${(props) => (props.completed ? '#d4edda' : '#f8f9fa')};
        transform: translateY(-1px);
    }

    &:active {
        transform: translateY(0);
    }
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    font-size: 1.4rem;

    &:hover {
        background-color: rgba(0, 0, 0, 0.05);
        transform: scale(1.1);
    }

    &:active {
        transform: scale(0.95);
    }
`

const SearchInputCustom = styled(SearchInput)`
    margin: 0;

    @media (max-width: ${Breakpoint.TABLET_L}) {
        margin-bottom: 16px;
    }
`

const SessionDescription = styled.div`
    color: #495057;
    margin-bottom: 14px;
    line-height: 1.6;
    font-size: 1.6rem;

    p {
        margin: 0 0 0.75em 0;
        &:last-child {
            margin-bottom: 0;
        }
    }
`

const IntensityBadge = styled.span<IntensityBadgeProps>`
    display: inline-block;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 1.4rem;
    font-weight: 500;
    background-color: ${({ intensity }) => {
        switch (intensity?.toLowerCase()) {
            case 'low':
                return '#e8f5e9'
            case 'medium':
                return '#fff3e0'
            case 'high':
                return '#ffebee'
            default:
                return '#f5f5f5'
        }
    }};
    color: ${({ intensity }) => {
        switch (intensity?.toLowerCase()) {
            case 'low':
                return '#2e7d32'
            case 'medium':
                return '#e65100'
            case 'high':
                return '#c62828'
            default:
                return '#424242'
        }
    }};
`

const StepsList = styled.div`
    margin-top: 16px;
    border-top: 1px solid #e9ecef;
    padding-top: 12px;

    h5 {
        margin: 0 0 12px 0;
        font-size: 0.9rem;
        color: #495057;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.5px;
    }
`

const StepItem = styled.div`
    background: #f8f9fa;
    border-left: 3px solid #3f51b5;
    padding: 10px 12px;
    margin-bottom: 8px;
    border-radius: 0 4px 4px 0;
    transition: all 0.2s ease;

    &:hover {
        background: #f1f3f5;
        transform: translateX(2px);
    }
`

const StepHeader = styled.div`
    display: flex;
    justify-content: space-between;
    margin-bottom: 4px;
`

const StepTitle = styled.span`
    font-weight: 500;
    color: #2c3e50;
    font-size: 1.6rem;
`

const StepTime = styled.span`
    font-size: 1.4rem;
    color: #6c757d;
    background: #e9ecef;
    padding: 2px 6px;
    border-radius: 4px;
    font-weight: 500;
`

const StepExerciseLink = styled.button`
    font-size: 1.3rem;
    color: #3f51b5;
    background: transparent;
    margin-top: 6px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    border: 1px solid #3f51b5;
    padding: 4px 10px;
    cursor: pointer;
    border-radius: 16px;
    font-weight: 500;
    transition: all 0.2s ease;

    &:hover {
        background: #3f51b5;
        color: white;
        transform: scale(1.05);
    }

    &:active {
        transform: scale(0.98);
    }
`

const GroupContainer = styled.div`
    margin: 0 12px;
    width: 500px;
    box-sizing: border-box;
    background: #ffffff;
    border-radius: 10px;
    border: 1px solid #e0e0e0;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
    padding: 20px;
    height: 100%;
    max-height: calc(100vh - 238px);
    display: flex;
    flex-direction: column;
    transition: all 0.2s ease;
    overflow-y: auto;
    cursor: grab;

    &:active {
        cursor: grabbing;
    }

    &::-webkit-scrollbar {
        width: 8px;
    }

    &::-webkit-scrollbar-thumb {
        background: #c1c1c1;
        border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb:hover {
        background: #a8a8a8;
    }

    &:hover {
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
    }

    @media (max-width: ${Breakpoint.TABLET_XS}) {
        min-width: 100%;
        max-width: 100%;
        width: 100%;
        height: calc(100vh - 112px);
        max-height: calc(100vh - 112px);
        margin: 0;
    }
`

const GroupHeader = styled.div`
    background: #f8f9fa;
    padding: 12px 16px;
    margin: -16px -16px 16px -16px;
    border-radius: 8px 8px 0 0;
    border-bottom: 1px solid #e0e0e0;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: center;
`

const GroupTitleWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`

const GroupTitle = styled.h2`
    margin: 0;
    font-size: 1.6rem;
    color: #2c3e50;
    font-weight: 600;
`

const MembersButton = styled(Button)`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 4px 10px;
    font-size: 1.3rem;
    border-radius: 16px;
    transition: all 0.2s ease;
    margin-top: 8px;

    &:hover {
        transform: translateY(-1px);
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }

    span {
        font-weight: 600;
    }
`

const MembersGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 16px;
    margin-top: 8px;
`

const SessionCount = styled.span`
    background: #e9ecef;
    color: #495057;
    padding: 4px 8px;
    border-radius: 12px;
    font-size: 1.4rem;
    font-weight: 500;
    margin-top: 8px;
`
